// The expandable sandbox row + detail used by BOTH the Infrastructure Request
// page ("My sandboxes") and the Catalog. It renders the collapsed table row
// and, when expanded, a tabbed detail (Overview / Logs / Outputs / DNS). In
// `catalog` mode it is read-only and shows only Overview + Outputs.
//
// Every value is wired to the real backend model — the mock's fake data is
// replaced by request.status → pipeline stage, request.outputs → the Outputs
// tab, the execution results → the Logs tab, and outputs.manualDnsRecords →
// the DNS tab.
import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import {
  createEnvVarEntry,
  entityNameFromRef,
  envVarEntriesToRecord,
  envVarEntriesToSecretKeys,
  defaultDeployFolder,
  environmentMeta,
  ENVIRONMENT_OPTIONS,
  formatCost,
  formatDateTime,
  getDestroyCountdown,
  isActiveStatus,
  recordToEnvVarEntries,
  reservedEnvKeysInEntries,
  retentionLabel,
  STATUS_META,
  type EnvVarEntry,
  type ExecutionResult,
  type SandboxRequest,
} from '../sandbox/model';
import { color, font } from './tokens';
import { Icon, type IconName } from './icons';
import { AnsiText } from './AnsiText';
import {
  Btn,
  ConfirmDialog,
  CopyButton,
  MetaTile,
  Tabs,
  type TabDef,
} from './primitives';
import { EnvEditor } from './EnvEditor';
import {
  pipelineCaption,
  pipelineFor,
  STAGES,
  stageStatusAt,
  type PipelineInfo,
  type StageStatus,
} from './pipeline';

// Grid template shared by the header row and every data row so columns align.
const ROW_COLS = '158px minmax(200px,1.5fr) 150px 112px 1.3fr 80px 84px 30px';
const ROW_MIN_WIDTH = 974;

const TONE_COLOR: Record<string, string> = {
  success: color.green,
  error: color.red,
  attention: color.amber,
  progress: color.amber,
  idle: color.textMuted,
};

type FeatureBadge = { icon: IconName; title: string };

const featureBadges = (r: SandboxRequest): FeatureBadge[] => {
  const f = r.features;
  const out: FeatureBadge[] = [];
  if (f?.apiEnabled) out.push({ icon: 'cloud', title: 'API' });
  if (f?.frontendEnabled) out.push({ icon: 'globe', title: 'Frontend' });
  if (f?.lambdaEnabled) out.push({ icon: 'function', title: 'Lambda' });
  if (f?.dbEnabled) out.push({ icon: 'database', title: 'Database' });
  if (f?.extraS3Enabled) out.push({ icon: 'bucket', title: 'Extra S3' });
  if (f?.vpnEnabled) out.push({ icon: 'shield-check', title: 'VPN' });
  return out;
};

const requesterName = (r: SandboxRequest) => {
  const first = r.account?.requestedOwnerName?.trim();
  const last = r.account?.requestedOwnerSurname?.trim();
  return [first, last].filter(Boolean).join(' ') || entityNameFromRef(r.ownerEntityRef);
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0]?.toUpperCase())
    .join('') || '?';

// Compact TTL such as "41h" / "6d" / "—", derived from the auto-destroy time.
const compactTtl = (r: SandboxRequest, nowMs: number): { text: string; low: boolean } => {
  const at = r.lifecycle.destroyAt;
  if (!r.lifecycle.autoDestroy || r.lifecycle.destroyedAt || !at) {
    return { text: '—', low: false };
  }
  const ms = new Date(at).getTime() - nowMs;
  if (Number.isNaN(ms)) return { text: '—', low: false };
  if (ms <= 0) return { text: 'due', low: true };
  const hours = Math.floor(ms / 3600000);
  const low = ms <= 24 * 3600000;
  if (hours >= 48) return { text: `${Math.floor(hours / 24)}d`, low };
  return { text: `${Math.max(hours, 1)}h`, low };
};

const failedStageIndex = (r: SandboxRequest): number => {
  const bad = (x?: ExecutionResult) => x && x.exitCode !== undefined && x.exitCode !== 0;
  if (bad(r.applyResult)) return 2;
  if (bad(r.dnsResult)) return 3;
  if (bad(r.planResult)) return 1;
  if (bad(r.accountApplyResult) || bad(r.accountPlanResult)) return 0;
  return 2;
};

const duration = (x?: ExecutionResult): string => {
  if (!x?.startedAt || !x.finishedAt) return '';
  const ms = new Date(x.finishedAt).getTime() - new Date(x.startedAt).getTime();
  if (Number.isNaN(ms) || ms < 0) return '';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

// --- small presentational bits -------------------------------------------
const Avatar = ({ text, size = 22 }: { text: string; size?: number }) => (
  <div
    style={{
      width: size,
      height: size,
      flex: 'none',
      borderRadius: '50%',
      background: 'linear-gradient(135deg,#3a3f6b,#2a2d3d)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      font: `600 ${size < 24 ? 9 : 11}px ${font.mono}`,
      color: '#c9c6ff',
    }}
  >
    {text}
  </div>
);

const stageNodeColor = (s: StageStatus, running: string): string =>
  s === 'done'
    ? color.green
    : s === 'running'
    ? running
    : s === 'error'
    ? color.red
    : '#3a414c';

const PipelineStepper = ({ info }: { info: PipelineInfo }) => {
  const donePct = info.ready ? 100 : (info.index / (STAGES.length - 1)) * 100;
  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        margin: '0 6px',
      }}
    >
      <div style={{ position: 'absolute', left: 14, right: 14, top: 14, height: 2, background: color.track }} />
      <div
        style={{
          position: 'absolute',
          left: 14,
          top: 14,
          height: 2,
          background: info.color,
          width: `calc((100% - 28px) * ${donePct / 100})`,
          transition: 'width .3s',
        }}
      />
      {STAGES.map((st, i) => {
        const s = stageStatusAt(info, i);
        const col = stageNodeColor(s, info.color);
        // Nodes sit on top of the connecting line, so their background MUST be
        // opaque or the coloured line shows through the circle. Composite the
        // status tint over the card surface via a solid gradient layer.
        const tint =
          s === 'done'
            ? 'rgba(70,178,106,.16)'
            : s === 'running'
            ? `color-mix(in oklab, ${info.color} 18%, transparent)`
            : s === 'error'
            ? 'rgba(224,108,117,.16)'
            : 'transparent';
        const bg = `linear-gradient(${tint},${tint}), ${color.surface}`;
        return (
          <div
            key={st.key}
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              zIndex: 1,
              flex: 1,
            }}
          >
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1.5px solid ${col}`,
                background: bg,
              }}
            >
              {s === 'done' ? (
                <Icon name="check" size={13} weight="bold" color={col} />
              ) : s === 'running' ? (
                <Icon name="spinner" size={13} color={col} spin />
              ) : s === 'error' ? (
                <Icon name="x" size={13} weight="bold" color={col} />
              ) : (
                <Icon name="circle" size={8} color={col} />
              )}
            </span>
            <span
              style={{ fontSize: 11, fontWeight: 600, color: s === 'pending' ? color.textDimmest : color.textSecondary }}
            >
              {st.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// --- Outputs helpers ------------------------------------------------------
const KVLine = ({
  label,
  value,
  labelW = 78,
  mono = true,
}: {
  label: string;
  value: string;
  labelW?: number;
  mono?: boolean;
}) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: `${labelW}px 1fr auto`,
      gap: 10,
      alignItems: 'center',
      padding: '8px 15px',
      borderBottom: `1px solid rgba(255,255,255,.04)`,
    }}
  >
    <span style={{ fontSize: 11, color: color.textDim }}>{label}</span>
    <span
      style={{
        font: `400 11.5px ${mono ? font.mono : font.sans}`,
        color: color.textSecondary,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {value}
    </span>
    <CopyButton value={value} iconOnly />
  </div>
);

const OutputCard = ({
  title,
  icon,
  note,
  children,
}: {
  title: string;
  icon?: IconName;
  note?: string;
  children: ReactNode;
}) => (
  <div
    style={{
      background: color.surface,
      border: `1px solid ${color.borderSoft}`,
      borderRadius: 11,
      overflow: 'hidden',
    }}
  >
    <div
      style={{
        padding: '11px 15px',
        borderBottom: `1px solid ${color.borderSoft}`,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      {icon && <Icon name={icon} size={14} color={color.accentLight} />}
      <span style={{ fontSize: 12.5, fontWeight: 600, color: color.textSecondary }}>{title}</span>
      {note && <span style={{ fontSize: 11, color: color.textDimmest, marginLeft: 'auto' }}>{note}</span>}
    </div>
    {children}
  </div>
);

// =========================================================================
export type SandboxListItemProps = {
  request: SandboxRequest;
  nowMs: number;
  expanded: boolean;
  onToggle: () => void;
  mode: 'infra' | 'catalog';
  youRef?: string;
  submitting?: boolean;
  onAction?: (path: string, body?: Record<string, unknown>) => void;
  // Admin-only: download the sandbox's full Terraform project.
  onExport?: () => Promise<void>;
};

export const SandboxListItem = ({
  request,
  nowMs,
  expanded,
  onToggle,
  mode,
  youRef,
  submitting = false,
  onAction,
  onExport,
}: SandboxListItemProps) => {
  const meta = STATUS_META[request.status];
  const stateColor = TONE_COLOR[meta.tone] ?? color.textMuted;
  const running = isActiveStatus(request.status);
  const info = useMemo(
    () => pipelineFor(request.status, failedStageIndex(request)),
    [request],
  );
  const badges = featureBadges(request);
  const owner = requesterName(request);
  const isYou =
    youRef !== undefined && request.ownerEntityRef.toLowerCase() === youRef.toLowerCase();
  const ttl = compactTtl(request, nowMs);
  // A `read` share makes the row read-only even on the Infrastructure Request
  // page; `write` shares, owners and admins get the full management controls.
  const readOnly = mode === 'catalog' || request.access === 'read';
  const canManage = mode === 'infra' && !readOnly;
  const sharedAccess =
    request.access === 'read' || request.access === 'write' ? request.access : undefined;

  const [tab, setTab] = useState<'overview' | 'logs' | 'outputs' | 'dns'>('overview');
  const [openLogs, setOpenLogs] = useState<Record<string, boolean>>({});
  const [envOpen, setEnvOpen] = useState(false);
  const [confirmDestroy, setConfirmDestroy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string>();
  const [apiEntries, setApiEntries] = useState<EnvVarEntry[]>(() => {
    const seed = recordToEnvVarEntries(
      request.features?.apiEnvVars ?? {},
      request.features?.apiSecretKeys ?? [],
    );
    return seed.length ? seed : [createEnvVarEntry()];
  });
  const [lambdaEntries, setLambdaEntries] = useState<EnvVarEntry[]>(() => {
    const seed = recordToEnvVarEntries(
      request.features?.lambdaEnvVars ?? {},
      request.features?.lambdaSecretKeys ?? [],
    );
    return seed.length ? seed : [createEnvVarEntry()];
  });

  // --- derived output collections ----------------------------------------
  const o = request.outputs;
  const f = request.features;
  const endpoints = (
    [
      ['API', o?.sandboxApiUrl],
      ['Web', o?.sandboxWebUrl],
    ] as Array<[string, string | undefined]>
  ).filter((e): e is [string, string] => Boolean(e[1]));
  const apiDeploy = (
    [
      ['ASG_NAME', o?.managedAsgName],
      ['AWS_ROLE_ARN', o?.githubRoleArn],
      ['DEPLOY_S3_BUCKET', o?.deployBucket],
      ['DEPLOY_FOLDER', f?.apiDeployFolder],
      ['ECR_URI', o?.ecrUri],
    ] as Array<[string, string | undefined]>
  ).filter((e): e is [string, string] => Boolean(e[1]));
  const webDeploy = (
    [
      ['AWS_DISTRIBUTION_ID', o?.cloudfrontDistributionId],
      ['AWS_ROLE_ARN', o?.githubRoleArn],
      ['AWS_S3_BUCKET', o?.webBucket],
      ['DEPLOY_FOLDER', f?.webDeployFolder],
    ] as Array<[string, string | undefined]>
  ).filter((e): e is [string, string] => Boolean(e[1]));
  const db = (
    [
      ['Host', o?.databaseEndpoint],
      ['Port', o?.databasePort],
      ['Database', o?.databaseName],
      ['User', o?.databaseAdminUser],
      ['Password', o?.databasePasswordSsmParameter],
    ] as Array<[string, string | undefined]>
  ).filter((e): e is [string, string] => Boolean(e[1]));
  const registry = (
    [
      ['ECR', o?.ecrUri],
      ['Bastion', o?.bastionInstanceId],
    ] as Array<[string, string | undefined]>
  ).filter((e): e is [string, string] => Boolean(e[1]));
  const dns = o?.manualDnsRecords ?? [];
  const hasOutputs =
    endpoints.length + apiDeploy.length + db.length + registry.length > 0;

  // --- logs stages -------------------------------------------------------
  const logStages = (
    [
      ['Vend', request.accountApplyResult ?? request.accountPlanResult, ['planning_account', 'creating_account', 'bootstrapping_account']],
      ['Plan', request.planResult, ['preparing_state', 'planning']],
      ['Apply', request.applyResult, ['applying']],
      ['DNS', request.dnsResult, ['awaiting_dns']],
      ['Destroy', request.destroyResult, ['destroying']],
    ] as Array<[string, ExecutionResult | undefined, string[]]>
  ).filter(([, r]) => Boolean(r)) as Array<[string, ExecutionResult, string[]]>;

  const canProvision = !(
    request.status === 'ready' ||
    request.status === 'destroyed' ||
    request.status === 'destroying'
  );
  // Destroy is allowed even while a run is in progress (it supersedes/cancels
  // the in-flight run) so a sandbox wedged mid-apply can still be torn down.
  // Only block it when already destroyed or a destroy is itself running.
  const canDestroy = !(
    request.status === 'destroyed' || request.status === 'destroying'
  );
  // Spelled out in the confirm dialog so the user sees exactly what disappears.
  const destroyItems = [
    f?.apiEnabled && 'API compute (EC2 / auto-scaling group) and the deployed build',
    f?.frontendEnabled && 'Frontend bucket, CloudFront distribution and its DNS records',
    f?.lambdaEnabled && 'Lambda functions and their configuration',
    f?.dbEnabled && 'RDS database and every row in it — no final snapshot is kept',
    f?.extraS3Enabled && 'Extra S3 bucket and all objects stored in it',
    'ECR images, secrets and environment variables',
    'The dedicated Terraform state backend for this sandbox',
  ].filter((x): x is string => Boolean(x));
  const accountCreated =
    request.accountApplyResult?.exitCode === 0 && Boolean(request.account?.accountId);
  const canEditEnv =
    !readOnly &&
    (f?.apiEnabled || f?.lambdaEnabled) &&
    accountCreated &&
    request.planResult?.exitCode === 0;

  const tabs: TabDef[] =
    canManage
      ? [
          { id: 'overview', label: 'Overview', icon: 'squares-four' },
          { id: 'logs', label: 'Logs', icon: 'list' },
          { id: 'outputs', label: 'Outputs', icon: 'export' },
          { id: 'dns', label: 'DNS', icon: 'globe', count: dns.length ? String(dns.length) : undefined },
        ]
      : [
          // Catalog is read-only, but Logs (run output + errors) are just as
          // useful for browsing/triage, so surface them here too.
          { id: 'overview', label: 'Overview', icon: 'squares-four' },
          { id: 'logs', label: 'Logs', icon: 'list' },
          { id: 'outputs', label: 'Outputs', icon: 'export' },
        ];

  const pipeMsg =
    request.status === 'destroying'
      ? 'OpenTofu destroy is running — live logs updating.'
      : request.status === 'ready'
      ? 'Infrastructure is live and healthy.'
      : request.status === 'failed'
      ? 'Last run failed — inspect the logs below.'
      : request.status === 'destroyed'
      ? 'This sandbox has been destroyed.'
      : running
      ? 'OpenTofu run in progress — live logs updating.'
      : 'Ready to apply.';
  const stepCaption = info.ready
    ? 'Step 5 of 5 · ready'
    : `Step ${info.index + 1} of 5 · ${STAGES[info.index].label.toLowerCase()}`;

  const rowSegments = STAGES.map((_st, i) => {
    const s = stageStatusAt(info, i);
    const bg =
      s === 'done'
        ? info.destroying
          ? color.blue
          : color.green
        : s === 'running'
        ? info.color
        : s === 'error'
        ? color.red
        : color.track;
    const animated =
      s === 'running'
        ? ';background-image:linear-gradient(90deg,transparent,rgba(255,255,255,.5),transparent);background-size:28px 100%'
        : '';
    return { bg, animated: Boolean(animated), s };
  });

  const rowStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: ROW_COLS,
    gap: 14,
    alignItems: 'center',
    padding: '13px 18px',
    cursor: 'pointer',
    background: expanded ? color.surfaceRaised : 'transparent',
    transition: 'background .12s',
  };

  const runEnvSave = () => {
    const body: Record<string, unknown> = {};
    if (f?.apiEnabled) {
      body.apiEnvVars = envVarEntriesToRecord(apiEntries);
      body.apiSecretKeys = envVarEntriesToSecretKeys(apiEntries);
    }
    if (f?.lambdaEnabled) {
      body.lambdaEnvVars = envVarEntriesToRecord(lambdaEntries);
      body.lambdaSecretKeys = envVarEntriesToSecretKeys(lambdaEntries);
    }
    onAction?.(`/api/sandbox-demo/requests/${request.id}/env`, body);
  };
  const envMeta = environmentMeta(request.environment);

  const costTitle = request.cost
    ? [
        `AWS account ${request.cost.accountId}`,
        `This month: ${formatCost(request.cost.monthToDate, request.cost.currency)}`,
        `Last month: ${formatCost(request.cost.lastMonth, request.cost.currency)}`,
        request.cost.sharedWith > 1
          ? `Shared by ${request.cost.sharedWith} live sandboxes in this account`
          : '',
        `Cost Explorer data as of ${formatDateTime(request.cost.fetchedAt)}`,
      ]
        .filter(Boolean)
        .join('\n')
    : 'No AWS account yet, or cost data unavailable';

  const exportButton = onExport ? (
    <Btn
      icon={exporting ? 'spinner' : 'download'}
      disabled={exporting}
      onClick={e => {
        e.stopPropagation();
        setExporting(true);
        setExportError(undefined);
        onExport()
          .catch(err => setExportError(err instanceof Error ? err.message : String(err)))
          .finally(() => setExporting(false));
      }}
    >
      {exporting ? 'Packing…' : 'Download Terraform'}
    </Btn>
  ) : null;

  const envInvalid =
    (f?.apiEnabled && reservedEnvKeysInEntries(apiEntries, f, 'api').length > 0) ||
    (f?.lambdaEnabled && reservedEnvKeysInEntries(lambdaEntries, f, 'lambda').length > 0);

  return (
    <div style={{ minWidth: ROW_MIN_WIDTH, borderBottom: `1px solid rgba(255,255,255,.05)` }}>
      {/* -------- collapsed row -------- */}
      <div onClick={onToggle} style={rowStyle}>
        {/* STATE — spinner first (fixed slot), then a label that truncates so it
            can never collide with the spinner on long statuses like
            "Validating account". */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
          {running ? (
            <Icon name="spinner" size={12} color={stateColor} spin style={{ flex: 'none' }} />
          ) : (
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                flex: 'none',
                background: stateColor,
              }}
            />
          )}
          <span
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: stateColor,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
            title={meta.label}
          >
            {meta.label}
          </span>
        </div>
        {/* SANDBOX */}
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#eaedf2',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {request.sandboxName}
            </span>
            {envMeta && (
              <span
                title={`${envMeta.label} — ${envMeta.sub}`}
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: '.04em',
                  color: envMeta.color,
                  background: envMeta.tint,
                  padding: '1px 6px',
                  borderRadius: 4,
                  whiteSpace: 'nowrap',
                  flex: 'none',
                }}
              >
                {envMeta.label.toUpperCase()}
              </span>
            )}
            {mode === 'catalog' && request.account && (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  color: color.textFaint,
                  background: '#181c24',
                  border: `1px solid ${color.border}`,
                  padding: '1px 6px',
                  borderRadius: 4,
                  whiteSpace: 'nowrap',
                  flex: 'none',
                }}
              >
                {request.account.projectType === 'low-code' ? 'Low Code' : 'Sandbox'}
              </span>
            )}
          </div>
          <div
            style={{
              font: `400 10.5px ${font.mono}`,
              color: color.textDimmest,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {request.account?.accountName ?? request.owner}
            {request.account?.accountId ? ` · ${request.account.accountId}` : ''}
          </div>
        </div>
        {/* OWNER */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
          <Avatar text={initials(owner)} />
          <span
            style={{
              fontSize: 12,
              color: color.textMuted,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {owner}
          </span>
          {isYou && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 600,
                color: color.accentSoft,
                background: 'color-mix(in oklab, #7b74ff 15%, transparent)',
                padding: '1px 5px',
                borderRadius: 4,
                flex: 'none',
              }}
            >
              YOU
            </span>
          )}
          {sharedAccess && (
            <span
              title={
                sharedAccess === 'write'
                  ? 'Shared with you — you can manage this sandbox'
                  : 'Shared with you — read-only'
              }
              style={{
                fontSize: 9,
                fontWeight: 600,
                color: sharedAccess === 'write' ? color.green : color.textFaint,
                background: color.surfaceChip,
                border: `1px solid ${color.border}`,
                padding: '1px 5px',
                borderRadius: 4,
                flex: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {sharedAccess === 'write' ? 'SHARED · EDIT' : 'SHARED · READ'}
            </span>
          )}
        </div>
        {/* PROVISIONED */}
        <div style={{ display: 'flex', gap: 5 }}>
          {badges.map(b => (
            <span
              key={b.title}
              title={b.title}
              style={{
                width: 22,
                height: 22,
                borderRadius: 6,
                background: color.surfaceChip,
                border: `1px solid ${color.borderSoft}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name={b.icon} size={12} color={color.textFaint} />
            </span>
          ))}
        </div>
        {/* PIPELINE */}
        <div style={{ minWidth: 0, paddingRight: 4 }}>
          <div style={{ display: 'flex', gap: 3, marginBottom: 5 }}>
            {rowSegments.map((g, i) => (
              <span
                key={i}
                style={{
                  flex: 1,
                  height: 4,
                  borderRadius: 3,
                  background: g.bg,
                  ...(g.animated ? { animation: 'ntt-barflow 1s linear infinite' } : {}),
                }}
              />
            ))}
          </div>
          <div style={{ font: `500 10.5px ${font.mono}`, color: color.textDim, whiteSpace: 'nowrap' }}>
            {pipelineCaption(request.status, info)}
          </div>
        </div>
        {/* COST */}
        <div
          title={costTitle}
          style={{ font: `500 11.5px ${font.mono}`, color: request.cost ? color.textMuted : color.textPlaceholder, whiteSpace: 'nowrap' }}
        >
          {request.cost ? formatCost(request.cost.monthToDate, request.cost.currency) : '—'}
          {request.cost && request.cost.sharedWith > 1 && (
            <span style={{ color: color.textDimmest }}> ·{request.cost.sharedWith}</span>
          )}
        </div>
        {/* TTL */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            font: `500 11.5px ${font.mono}`,
            color: ttl.low ? color.amber : color.textMuted,
          }}
        >
          <Icon name="clock" size={12} />
          {ttl.text}
        </div>
        {/* caret */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: color.textDimmer }}>
          <Icon name={expanded ? 'caret-up' : 'caret-down'} size={14} />
        </div>
      </div>

      {/* -------- expanded detail -------- */}
      {expanded && (
        <div
          style={{
            background: '#0b0d11',
            borderTop: `1px solid color-mix(in oklab, #7b74ff 22%, transparent)`,
          }}
        >
          <Tabs tabs={tabs} active={tab} onSelect={id => setTab(id as typeof tab)} />
          <div style={{ padding: 18 }}>
            {/* OVERVIEW */}
            {tab === 'overview' && (
              <>
                <div
                  style={{
                    background: color.surface,
                    border: `1px solid ${color.borderSoft}`,
                    borderRadius: 12,
                    padding: '18px 20px',
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 16,
                      gap: 12,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                      {running ? (
                        <Icon name="spinner" size={15} color={info.color} spin />
                      ) : (
                        <Icon
                          name={info.ready ? 'check' : info.failed ? 'x' : 'circle'}
                          size={15}
                          weight={info.ready || info.failed ? 'bold' : 'regular'}
                          color={stateColor}
                        />
                      )}
                      <span style={{ fontSize: 13, color: color.textSecondary }}>{pipeMsg}</span>
                    </div>
                    <span style={{ font: `500 12px ${font.mono}`, color: color.textDim, whiteSpace: 'nowrap' }}>
                      {stepCaption}
                    </span>
                  </div>
                  <PipelineStepper info={info} />
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3,1fr)',
                    gap: 10,
                    marginBottom: canManage ? 16 : 0,
                  }}
                >
                  <MetaTile k="AWS account" v={request.account?.accountId ?? '—'} />
                  <MetaTile k="Region" v="us-east-1" />
                  <MetaTile k="VPC CIDR" v={f?.vpcCidr ?? '10.1.0.0/16'} />
                  <MetaTile k="Instance type" v={f?.apiInstanceType ?? '—'} />
                  <MetaTile k="Created" v={formatDateTime(request.createdAt)} />
                  <MetaTile
                    k="Retention"
                    v={
                      request.lifecycle.destroyedAt
                        ? 'destroyed'
                        : request.lifecycle.autoDestroy && request.lifecycle.destroyAt
                        ? getDestroyCountdown(request.lifecycle.destroyAt, nowMs)
                        : retentionLabel(request.lifecycle.ttlOption)
                    }
                  />
                  <MetaTile k="Owner" v={owner} />
                  <MetaTile
                    k="Environment"
                    v={
                      canManage ? (
                        <select
                          value={request.environment ?? ''}
                          disabled={submitting}
                          onClick={e => e.stopPropagation()}
                          onChange={e =>
                            onAction?.(`/api/sandbox-demo/requests/${request.id}/environment`, {
                              environment: e.target.value,
                            })
                          }
                          style={{
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            font: `500 12.5px ${font.mono}`,
                            color: envMeta?.color ?? color.textDim,
                            cursor: 'pointer',
                          }}
                        >
                          {!request.environment && <option value="">not set — choose</option>}
                          {ENVIRONMENT_OPTIONS.map(opt => (
                            <option key={opt.id} value={opt.id}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        envMeta?.label ?? '—'
                      )
                    }
                  />
                  <MetaTile
                    k={request.cost && request.cost.sharedWith > 1 ? `Account cost · this month (${request.cost.sharedWith} sandboxes)` : 'Cost · this month'}
                    v={request.cost ? formatCost(request.cost.monthToDate, request.cost.currency) : '—'}
                  />
                  <MetaTile
                    k="Cost · last month"
                    v={request.cost ? formatCost(request.cost.lastMonth, request.cost.currency) : '—'}
                  />
                </div>

                {!canManage && exportButton && (
                  <div style={{ display: 'flex', marginTop: 16 }}>{exportButton}</div>
                )}
                {exportError && (
                  <div style={{ marginTop: 10, fontSize: 12, color: color.red }}>{exportError}</div>
                )}
                {canManage && (
                  <>
                    <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', alignItems: 'center' }}>
                      {canProvision && (
                        <Btn
                          variant="primary"
                          icon="play"
                          iconWeight="bold"
                          disabled={submitting || running}
                          onClick={e => {
                            e.stopPropagation();
                            onAction?.(`/api/sandbox-demo/requests/${request.id}/provision`);
                          }}
                        >
                          {running ? 'Provisioning…' : 'Apply infrastructure'}
                        </Btn>
                      )}
                      <Btn
                        icon="plan"
                        onClick={e => {
                          e.stopPropagation();
                          setTab('logs');
                        }}
                      >
                        View plan
                      </Btn>
                      {exportButton}
                      <div style={{ flex: 1 }} />
                      <Btn
                        variant="danger"
                        icon="trash"
                        disabled={submitting || !canDestroy}
                        onClick={e => {
                          e.stopPropagation();
                          setConfirmDestroy(true);
                        }}
                      >
                        {request.status === 'destroying' ? 'Destroying…' : 'Destroy'}
                      </Btn>
                    </div>

                    <ConfirmDialog
                      open={confirmDestroy}
                      title="Destroy this sandbox permanently?"
                      confirmLabel="Destroy everything"
                      cancelLabel="Keep it"
                      busy={submitting}
                      onCancel={() => setConfirmDestroy(false)}
                      onConfirm={() => {
                        setConfirmDestroy(false);
                        onAction?.(`/api/sandbox-demo/requests/${request.id}/destroy`);
                      }}
                    >
                      <p style={{ margin: '0 0 12px' }}>
                        OpenTofu will tear down{' '}
                        <strong style={{ color: color.textStrong }}>
                          {request.sandboxName}
                        </strong>{' '}
                        in AWS account{' '}
                        <span style={{ font: `500 12px ${font.mono}`, color: color.textSecondary }}>
                          {request.account?.accountId ?? 'pending'}
                        </span>
                        . The infrastructure and every piece of data on it are deleted{' '}
                        <strong style={{ color: color.textStrong }}>forever</strong> — this
                        cannot be undone and there is no backup to restore from.
                      </p>
                      <ul
                        style={{
                          margin: '0 0 12px',
                          padding: '0 0 0 16px',
                          display: 'grid',
                          gap: 4,
                        }}
                      >
                        {destroyItems.map(item => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                      {running && (
                        <div
                          style={{
                            display: 'flex',
                            gap: 8,
                            padding: '9px 11px',
                            borderRadius: 9,
                            background: 'rgba(217,164,65,.1)',
                            border: `1px solid rgba(217,164,65,.28)`,
                            color: color.amber,
                          }}
                        >
                          <Icon name="warning" size={14} weight="bold" color={color.amber} />
                          <span>
                            A run is currently in progress — destroying cancels it and tears
                            down whatever it has already created.
                          </span>
                        </div>
                      )}
                    </ConfirmDialog>

                    {canEditEnv && (
                      <div
                        style={{
                          marginTop: 16,
                          border: `1px solid ${color.borderSoft}`,
                          borderRadius: 11,
                          overflow: 'hidden',
                        }}
                      >
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            setEnvOpen(v => !v);
                          }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 9,
                            padding: '12px 15px',
                            background: 'none',
                            border: 'none',
                            color: color.textSecondary,
                            fontSize: 12.5,
                            fontWeight: 600,
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          Environment variables — edit &amp; re-apply
                          <Icon
                            name={envOpen ? 'caret-up' : 'caret-down'}
                            size={12}
                            color={color.textDimmer}
                            style={{ marginLeft: 'auto' }}
                          />
                        </button>
                        {envOpen && (
                          <div
                            style={{
                              padding: '0 15px 15px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 12,
                            }}
                            onClick={e => e.stopPropagation()}
                          >
                            <span style={{ fontSize: 11.5, color: color.textDim }}>
                              Re-applies the infrastructure with the new values and replaces the
                              running instance so they take effect.
                            </span>
                            {f?.apiEnabled && (
                              <EnvEditor
                                entries={apiEntries}
                                setEntries={setApiEntries}
                                features={f}
                                scope="api"
                              />
                            )}
                            {f?.lambdaEnabled && (
                              <EnvEditor
                                entries={lambdaEntries}
                                setEntries={setLambdaEntries}
                                features={f}
                                scope="lambda"
                              />
                            )}
                            <div>
                              <Btn
                                variant="primary"
                                small
                                disabled={submitting || running || Boolean(envInvalid)}
                                onClick={e => {
                                  e.stopPropagation();
                                  runEnvSave();
                                }}
                              >
                                Save &amp; re-apply
                              </Btn>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* LOGS (available in both infra and catalog — read-only either way) */}
            {tab === 'logs' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {logStages.length === 0 && (
                  <div style={{ fontSize: 12.5, color: color.textDim }}>
                    No runs yet.
                  </div>
                )}
                {logStages.map(([label, res, statuses]) => {
                  const isRun = statuses.includes(request.status);
                  const isErr = res.exitCode !== undefined && res.exitCode !== 0;
                  const open = openLogs[label] ?? (isRun || isErr);
                  const dotColor = isErr ? color.red : isRun ? color.amber : color.green;
                  return (
                    <div
                      key={label}
                      style={{
                        border: `1px solid ${color.borderSoft}`,
                        borderRadius: 10,
                        overflow: 'hidden',
                        background: color.surface,
                      }}
                    >
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          setOpenLogs(s => ({ ...s, [label]: !open }));
                        }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 11,
                          padding: '11px 14px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        <span
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: 'none',
                            border: `1.5px solid ${dotColor}`,
                            background: isErr
                              ? 'rgba(224,108,117,.14)'
                              : isRun
                              ? `color-mix(in oklab, ${color.amber} 16%, transparent)`
                              : 'rgba(70,178,106,.14)',
                          }}
                        >
                          {isErr ? (
                            <Icon name="x" size={11} weight="bold" color={dotColor} />
                          ) : isRun ? (
                            <Icon name="spinner" size={11} color={dotColor} spin />
                          ) : (
                            <Icon name="check" size={11} weight="bold" color={dotColor} />
                          )}
                        </span>
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: color.textSecondary }}>
                          {label}
                        </span>
                        <span style={{ fontSize: 11, color: color.textDimmer, fontFamily: font.mono }}>
                          {duration(res)}
                        </span>
                        <Icon
                          name={open ? 'caret-up' : 'caret-down'}
                          size={12}
                          color={color.textDimmer}
                          style={{ marginLeft: 'auto' }}
                        />
                      </button>
                      {open && (
                        <div
                          style={{
                            borderTop: `1px solid ${color.borderSoft}`,
                            background: color.terminal,
                            padding: '12px 15px',
                            fontFamily: font.mono,
                            fontSize: 11.5,
                            lineHeight: 1.7,
                            maxHeight: 320,
                            overflow: 'auto',
                          }}
                        >
                          {res.summary && (
                            <div style={{ color: color.textMuted, marginBottom: 8 }}>{res.summary}</div>
                          )}
                          <pre
                            style={{
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              color: '#a7aeba',
                              fontFamily: font.mono,
                            }}
                          >
                            <AnsiText text={res.output} fallback="Waiting for logs…" />
                          </pre>
                          {isRun && (
                            <div style={{ color: color.textDim }}>
                              <span className="ntt-cur">▌</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* OUTPUTS */}
            {tab === 'outputs' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {!hasOutputs && (
                  <div style={{ fontSize: 12.5, color: color.textDim }}>
                    Outputs appear here after a successful apply.
                  </div>
                )}
                {endpoints.length > 0 && (
                  <OutputCard title="Endpoints">
                    {endpoints.map(([label, value]) => (
                      <div
                        key={label}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '120px 1fr auto',
                          gap: 12,
                          alignItems: 'center',
                          padding: '10px 15px',
                          borderBottom: `1px solid rgba(255,255,255,.04)`,
                        }}
                      >
                        <span style={{ fontSize: 11.5, color: color.textDim }}>{label}</span>
                        <a
                          href={value}
                          target="_blank"
                          rel="noreferrer"
                          onClick={e => e.stopPropagation()}
                          style={{
                            font: `400 12px ${font.mono}`,
                            color: color.accentLight,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            textDecoration: 'none',
                          }}
                        >
                          {value}
                        </a>
                        <CopyButton value={value} />
                      </div>
                    ))}
                  </OutputCard>
                )}
                {apiDeploy.length > 0 && (
                  <OutputCard
                    title="API deploy values (CI/CD)"
                    icon="git-branch"
                    note={`Copy into your pipeline variables under ${f?.apiDeployFolder || defaultDeployFolder(request.environment)} folder`}
                  >
                    {apiDeploy.map(([label, value]) => (
                      <div
                        key={label}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '190px 1fr auto',
                          gap: 12,
                          alignItems: 'center',
                          padding: '9px 15px',
                          borderBottom: `1px solid rgba(255,255,255,.04)`,
                        }}
                      >
                        <span style={{ font: `500 11.5px ${font.mono}`, color: color.textFaint }}>{label}</span>
                        <span
                          style={{
                            font: `400 12px ${font.mono}`,
                            color: color.textSecondary,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {value}
                        </span>
                        <CopyButton value={value} iconOnly />
                      </div>
                    ))}
                  </OutputCard>
                )}
                {webDeploy.length > 0 && f?.frontendEnabled && (
                  <OutputCard
                    title="Web deploy values (CI/CD)"
                    icon="git-branch"
                    note={`Copy into your pipeline variables under ${f?.webDeployFolder || defaultDeployFolder(request.environment)} folder`}
                  >
                    {webDeploy.map(([label, value]) => (
                      <div
                        key={label}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '190px 1fr auto',
                          gap: 12,
                          alignItems: 'center',
                          padding: '9px 15px',
                          borderBottom: `1px solid rgba(255,255,255,.04)`,
                        }}
                      >
                        <span style={{ font: `500 11.5px ${font.mono}`, color: color.textFaint }}>{label}</span>
                        <span
                          style={{
                            font: `400 12px ${font.mono}`,
                            color: color.textSecondary,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {value}
                        </span>
                        <CopyButton value={value} iconOnly />
                      </div>
                    ))}
                  </OutputCard>
                )}
                {(db.length > 0 || registry.length > 0) && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    {db.length > 0 && (
                      <OutputCard title="Database">
                        {db.map(([label, value]) => (
                          <KVLine key={label} label={label} value={value} />
                        ))}
                      </OutputCard>
                    )}
                    {registry.length > 0 && (
                      <OutputCard title="Registry & access">
                        {registry.map(([label, value]) => (
                          <KVLine key={label} label={label} value={value} />
                        ))}
                      </OutputCard>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* DNS */}
            {tab === 'dns' && canManage && (
              <OutputCard
                title="DNS records"
                note="Auto-managed in the platform zone; add to your own zone if delegated"
              >
                {dns.length === 0 ? (
                  <div style={{ padding: '14px 15px', fontSize: 12, color: color.textDim }}>
                    No DNS records emitted yet.
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '70px 1.2fr 1.6fr 96px auto',
                        gap: 12,
                        padding: '9px 15px',
                        borderBottom: `1px solid ${color.borderSoft}`,
                        font: `600 10px ${font.mono}`,
                        letterSpacing: '.08em',
                        color: color.textPlaceholder,
                      }}
                    >
                      <span>TYPE</span>
                      <span>NAME</span>
                      <span>VALUE</span>
                      <span>STATUS</span>
                      <span />
                    </div>
                    {dns.map((d, i) => {
                      const active = request.status === 'ready';
                      return (
                        <div
                          key={`${d.name}-${i}`}
                          title={d.purpose}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '70px 1.2fr 1.6fr 96px auto',
                            gap: 12,
                            alignItems: 'center',
                            padding: '9px 15px',
                            borderBottom: `1px solid rgba(255,255,255,.04)`,
                          }}
                        >
                          <span style={{ font: `600 11px ${font.mono}`, color: color.accentSoft }}>{d.type}</span>
                          <span
                            style={{
                              font: `400 11.5px ${font.mono}`,
                              color: color.textSecondary,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {d.name}
                          </span>
                          <span
                            style={{
                              font: `400 11.5px ${font.mono}`,
                              color: color.textFaint,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {d.value}
                          </span>
                          <span
                            style={{
                              font: `600 10.5px ${font.mono}`,
                              padding: '2px 8px',
                              borderRadius: 5,
                              textAlign: 'center',
                              color: active ? color.greenBright : color.amber,
                              background: active ? 'rgba(70,178,106,.12)' : 'rgba(217,164,65,.12)',
                            }}
                          >
                            {active ? 'active' : 'pending'}
                          </span>
                          <CopyButton value={d.value} iconOnly />
                        </div>
                      );
                    })}
                  </>
                )}
              </OutputCard>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Header row for the sandbox table (STATE / SANDBOX / ...). Exported so both
// pages render an identical, column-aligned header above the rows.
export const SandboxTableHeader = () => (
  <div
    style={{
      minWidth: ROW_MIN_WIDTH,
      display: 'grid',
      gridTemplateColumns: ROW_COLS,
      gap: 14,
      padding: '9px 18px',
      borderBottom: `1px solid ${color.borderSoft}`,
      font: `600 10px ${font.mono}`,
      letterSpacing: '.09em',
      color: color.textPlaceholder,
    }}
  >
    <span>STATE</span>
    <span>SANDBOX</span>
    <span>OWNER</span>
    <span>PROVISIONED</span>
    <span>PIPELINE</span>
    <span title="AWS spend of the sandbox's account this month">COST</span>
    <span>TTL</span>
    <span />
  </div>
);
